import { Text } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PREVIEW_MESSAGE_TYPE, buildAppPreviewDocument, buildPreviewDocument } from '../helpers/previewDocument';
import { CONTENT_SLACK, clampContentHeight, getPreviewFit } from '../helpers/previewFit';
import classes from './SdkPreview.module.css';

const INITIAL_CONTENT_HEIGHT = 600;

// How long to let a burst of payload changes (a keystroke in the Custom CSS
// editor, a colour picker drag) settle before it is worth tearing down and
// re-running the SDK bundle for.
const PAYLOAD_SETTLE_MS = 300;

const BUNDLE_FAILURE =
  'Unable to show preview: the preview code could not be loaded from releva.ai. It may be blocked in this browser, or temporarily unavailable.';

// A block on its own, rendered by the SDK in a frame of its own. Nothing of the
// shop is around it — no theme stylesheet, no fonts — so the page is an
// approximation by construction, but the block itself is not: it is built by the
// same code that will build it on the shop. The one exception is `appChrome`
// below, where there is no such code to run: the chrome the native app SDKs
// draw is built here.
//
// The frame is sandboxed *without* `allow-same-origin`, so its document has an
// opaque origin: it cannot reach the admin's cookies or storage even if
// something side-effecting ever slips into the bundle. That also costs the
// parent its access to `contentDocument`, which is where the height used to be
// measured, so the height and any failure come back by `postMessage` from the
// script the document carries.
const SdkPreview = ({
  method,
  payload,
  scope,
  baseUrl,
  viewportHeight,
  // 'desktop' | 'mobile' — which device the block is being previewed on. Only
  // the width it implies: the caller sizes the frame's height, since what a
  // viewport height means depends on the block (see `SHOP_VIEWPORT_HEIGHT`).
  viewport,
  // Draw the chrome the native app SDKs draw — card, overlay and close control
  // — around the design's own content, instead of running the web SDK bundle.
  // The two differ by an explicit product decision, so an author previewing a
  // block that is also served to an app has no other way to see it.
  appChrome = false,
  // False when something else already draws the window around this — a device
  // frame — so the viewport does not put a second border just inside it.
  withBorder = true,
  errorLabel = 'Unable to show preview',
}) => {
  const { ref: holderRef, width: availableWidth } = useElementSize();
  const [contentHeight, setContentHeight] = useState(INITIAL_CONTENT_HEIGHT);
  const [mobileOnly, setMobileOnly] = useState(false);
  const [failure, setFailure] = useState(null);
  // A script the block carries that threw *after* the markup was on screen.
  // Not a failure — the preview below is real — so it is said beside it rather
  // than instead of it.
  const [scriptError, setScriptError] = useState(null);
  const frameRef = useRef(null);

  // `payload` is a fresh object every render — the caller reads it out of
  // form state — so it is compared by its serialised form rather than by
  // identity. That string doubles as the debounce key: while it keeps
  // changing (a keystroke, a colour drag) the timeout below keeps getting
  // cancelled and re-armed, and `settledPayload` only catches up once it
  // stops. Without this, every one of those events would set `srcDoc` to a
  // new string, which React writes to the `srcdoc` attribute, which
  // navigates the frame and re-executes the SDK bundle from scratch.
  const payloadKey = JSON.stringify(payload);
  const [settledPayload, setSettledPayload] = useState(payload);

  useEffect(() => {
    const timeoutId = setTimeout(() => setSettledPayload(payload), PAYLOAD_SETTLE_MS);

    return () => clearTimeout(timeoutId);
    // `payload` itself is intentionally omitted: it is a new object every
    // render, which would re-arm the timeout on every render rather than only
    // when its content actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey]);

  const srcDoc = useMemo(
    () =>
      appChrome
        ? buildAppPreviewDocument({ payload: settledPayload, baseUrl })
        : buildPreviewDocument({ method, payload: settledPayload, baseUrl, parentOrigin: window.location.origin }),
    [appChrome, method, settledPayload, baseUrl]
  );

  // A new document renders from scratch, so a failure the previous one reported
  // is not what is on screen any more.
  useEffect(() => {
    setFailure(null);
    setScriptError(null);
  }, [srcDoc]);

  useEffect(() => {
    const handleMessage = (event) => {
      // An opaque origin posts `event.origin` as the string "null", which
      // identifies nothing, so the frame's own window is the identity here.
      if (event.source !== frameRef.current?.contentWindow || event.data?.type !== PREVIEW_MESSAGE_TYPE) {
        return;
      }

      if (event.data.bundleFailed) {
        setFailure(BUNDLE_FAILURE);
        return;
      }

      if (event.data.error) {
        setFailure(`${errorLabel}: ${event.data.error}.`);
        return;
      }

      setMobileOnly(!!event.data.mobileOnly);
      setScriptError(event.data.scriptError || null);

      const measured = clampContentHeight(event.data.height + CONTENT_SLACK);

      // Ignore sub-pixel noise: resizing the frame re-runs the layout inside it,
      // which measures again, and a 0.5px difference would never settle.
      setContentHeight((previous) => (Math.abs(previous - measured) > 1 ? measured : previous));
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [errorLabel]);

  if (failure) {
    return <Text size="sm">{failure}</Text>;
  }

  const frameHeight = viewportHeight || contentHeight;

  const { designWidth, scale, footprintWidth, footprintHeight, boxHeight } = getPreviewFit({
    scope,
    mobileOnly,
    viewport,
    availableWidth,
    contentHeight: frameHeight,
  });

  return (
    <div className={classes.holder} ref={holderRef}>
      {scriptError && (
        <Text size="xs" c="dimmed" mb="xs">
          Some of this block&apos;s own scripts did not run here: {scriptError}. Whatever they build — a slider, a
          countdown — is missing from the preview, not from your site.
        </Text>
      )}
      <div className={classes.viewport} style={{ height: boxHeight }} data-flush={!withBorder || undefined}>
        <div className={classes.footprint} style={{ width: footprintWidth, height: footprintHeight }}>
          <div className={classes.scaler} style={{ transform: `scale(${scale})` }}>
            <iframe
              title="Preview"
              className={classes.frame}
              ref={frameRef}
              width={designWidth}
              height={frameHeight}
              sandbox="allow-scripts"
              srcDoc={srcDoc}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SdkPreview;
